/**
 * License Intelligence Dashboard
 * Main page component for license management and analytics
 */

import React, { useEffect, useState } from 'react';
import { Logger } from '@saviynt/common';

import {
  AddLicensePanel,
  fetchHeaderData,
  fetchLicenseDetails,
  Findings,
  GraphicalView,
  Header,
  LicenseDetailsPanel,
  type LicenseFormData,
  LicenseInventoryContent,
  Tabs,
} from '../../features/LicenseManagement';
import type { LicenseDetail } from '../../features/LicenseManagement/components/Dashboard/LicenseDetailsPanel/type';
import {
  VIEW_MODES,
  type ViewMode,
} from '../../features/LicenseManagement/components/Dashboard/Tabs/type';
import { KPICards } from '../../features/LicenseManagement/components/Shared';
import kpiCardsData from '../../features/LicenseManagement/components/Shared/KPICards/mockData';

import './LicenseIntelligence.css';

/**
 * Main page component for the License Intelligence dashboard.
 * Sets a dark theme on mount, fetches header metadata, and orchestrates
 * view-mode switching (list, graphical, SAVI) alongside add-license and
 * license-details side panels.
 * @returns {React.ReactElement} The rendered License Intelligence dashboard
 */
function LicenseIntelligence(): React.ReactElement {
  const [complianceScore, setComplianceScore] = useState(60);
  const [lastSyncDate, setLastSyncDate] = useState('Jan 12, 2026 1:24 PM');
  const [viewMode, setViewMode] = useState<ViewMode>(VIEW_MODES.LIST);
  const [isAddLicensePanelOpen, setIsAddLicensePanelOpen] = useState(false);
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<
    LicenseDetail | undefined
  >();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');

    return () => {
      document.documentElement.removeAttribute('data-theme');
    };
  }, []);

  useEffect(() => {
    const loadHeaderData = async (): Promise<void> => {
      try {
        const headerData = await fetchHeaderData();

        setComplianceScore(headerData.complianceScore);
        setLastSyncDate(headerData.lastSyncDate);
      } catch (error) {
        // Keep default values on error
      }
    };

    loadHeaderData().catch(() => {});
  }, []);

  const handleAddLicense = (): void => {
    setIsAddLicensePanelOpen(true);
  };

  const handleAddLicensePanelClose = (): void => {
    setIsAddLicensePanelOpen(false);
  };

  const handleAddLicenseSave = (licenseData: LicenseFormData): void => {
    Logger.log('License saved:', licenseData);
  };

  const handleAddWidget = (): void => {
    Logger.log('Add Widget clicked');
  };

  const handleSortChange = (sortOption: string): void => {
    Logger.log('Sort changed to:', sortOption);
  };

  const handleSelectLicense = (licenseId: string): void => {
    Logger.log('License selected:', licenseId);

    fetchLicenseDetails(licenseId)
      .then((licenseDetail) => {
        if (licenseDetail && typeof licenseDetail === 'object') {
          setSelectedLicense(licenseDetail as LicenseDetail);
          setIsDetailsPanelOpen(true);
        }
      })
      .catch(() => {});
  };

  /** Renders the main content area based on view mode */
  const renderContent = (): React.ReactElement => {
    if (viewMode === VIEW_MODES.SAVI) {
      return (
        <div className='license-intelligence-scrollArea'>
          <div className='saviPilotLayout'>
            <div className='saviPilotTopRow'>
              <div className='saviPilotChartArea'>
                <GraphicalView saviChartOnly />
              </div>
              <div className='saviPilotFindingsSidebar'>
                <Findings onClose={() => setViewMode(VIEW_MODES.GRAPHICAL)} />
              </div>
            </div>
            <div className='saviPilotDivider' />
            <div className='saviPilotWidgetsSection'>
              <GraphicalView saviWidgetsOnly />
            </div>
          </div>
        </div>
      );
    }

    if (viewMode === VIEW_MODES.GRAPHICAL) {
      return (
        <div className='license-intelligence-scrollArea'>
          <GraphicalView />
        </div>
      );
    }

    return (
      <div className='license-intelligence-scrollArea'>
        <LicenseInventoryContent onSelectLicense={handleSelectLicense} />
      </div>
    );
  };

  return (
    <>
      <div className='license-intelligence' data-theme='dark'>
        <div className='license-intelligence-header'>
          <Header
            complianceScore={complianceScore}
            lastSyncDate={lastSyncDate}
            onAddLicense={handleAddLicense}
            onAddWidget={handleAddWidget}
          />

          <KPICards cards={kpiCardsData} />

          <Tabs
            onViewModeChange={setViewMode}
            viewMode={viewMode}
            sortBy='By Annual Cost'
            onSortChange={handleSortChange}
            isFindingsOpen={viewMode === VIEW_MODES.SAVI}
            onFindingsToggle={() => setViewMode(VIEW_MODES.SAVI)}
          />
        </div>

        {renderContent()}
      </div>

      <AddLicensePanel
        isOpen={isAddLicensePanelOpen}
        onClose={handleAddLicensePanelClose}
        onSave={handleAddLicenseSave}
      />

      <LicenseDetailsPanel
        isOpen={isDetailsPanelOpen}
        onClose={() => setIsDetailsPanelOpen(false)}
        licenseData={selectedLicense}
      />
    </>
  );
}

export default LicenseIntelligence;
